import { useEffect, useState, useCallback } from "react";

// for validation logic
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { discountSchema } from "@/validationSchemas/discountSchema";

// hooks and utils
import useDiscountFormLoader from "../hooks/useDiscountFormLoader";
import useSaveDiscount from "../hooks/useSaveDiscount";
import { useUnsavedChangesPrompt } from "../../products/hooks/useUnsavedChangesPrompt";
import useDiscountFormStore from "@/stores/useDiscountFormStore";
import useSaveBarStore from "@/stores/useSaveBarStore";

// import the section of the form
import DiscountDetailsSection from "./DiscountDetailsSection";
import DiscountDateSection from "./DiscountDateSection";

const DiscountPageForm = ({ discountId }) => {
  const isEditMode = Boolean(discountId);
  const { showSaveBar, hideSaveBar } = useSaveBarStore();

  const { saveDiscount } = useSaveDiscount({ isEditMode, discountId });

  const { isSaving } = useDiscountFormStore();
  const { setDiscountData } = useDiscountFormStore();

  const onSubmit = async (data) => {
    try {
      const result = await saveDiscount(data);
      setDiscountData(result); // Save latest state
      console.log("Saved Discount:", result);
    } catch (err) {
      console.error("Save Discount Failed:", err);
    }
  };

  const [loading, setLoading] = useState(isEditMode);
  const [error, setError] = useState(null);

  // fetch and normalize discount data when editing
  const {
    data: discountData,
    isLoading: isFetching,
    error: fetchError,
  } = useDiscountFormLoader(discountId);

  const {

    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isDirty },

  } = useForm({

    resolver: zodResolver(discountSchema),
    defaultValues: {
      discountType: "",
      discountValue: 0,
      discountCode: "",
      autoApplyDiscount: false,
      discountStartDate: "",
      discountEndDate: "",
    },

  });

  // Prelaod form when in edit mode and discount data is available
  useEffect(() => {
    if (isEditMode && discountData) {
      reset(discountData);
      setDiscountData(discountData); // Put it in the store
      setLoading(false);
    }
    if (fetchError) {
      setError(fetchError);
      setLoading(false);
    }
  }, [isEditMode, discountData, fetchError, reset]);

  const handleSave = useCallback(handleSubmit(onSubmit), [onSubmit]);
  const handleDiscard = useCallback(() => reset(discountData), [reset, discountData]);
  useEffect(() => {
    if (isDirty) {
      showSaveBar(handleSave, handleDiscard);
    } else {
      hideSaveBar();
    }
  }, [isDirty]);

  // Prevent tab close or browser refresh if form has unsaves changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  // Block naviagetion if thers's unsaves changes
  const {
    showPrompt,
    confirmNavigation,
    cancelNavigation
  } = useUnsavedChangesPrompt(isDirty);

  // fallback UIs
  if (loading || isFetching) return <p className="p-4">Loading discount...</p>;
  if (error) return <p className="p-4 text-red-500">Error: {error}</p>;

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-[1em]">
        <DiscountDetailsSection 
          register={register} 
          errors={errors} 
          watch={watch} 
        />
        
        <DiscountDateSection 
          register={register} 
          errors={errors} 
          watch={watch} 
        />

        {/* Update or create buttons */}
        {/* <div className="flex gap-3 mt-4">
          <button
            type="submit"
            className="mt-4 w-fit px-5 py-2 bg-black text-white rounded hover:bg-gray-800 transition"
          >
            {isEditMode ? "Update Discount" : "Create Discount"}
          </button>

          <button
            type="button"
            className="px-5 py-2 bg-gray-100 text-black rounded hover:bg-gray-200 transition"
            onClick={() => reset()}
          >
            Cancel
          </button>
        </div> */}
      </form>

      {showPrompt && (
        <div className="fixed inset-0 bg-[#000]/40 z-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-md shadow-md max-w-sm w-full">
            <p className="text-gray-800 mb-4 text-sm">
              You have unsaved changes. Are you sure you want to leave this page?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={cancelNavigation}
                className="text-sm px-4 py-2 rounded border border-gray-300 hover:bg-gray-100"
              >
                Stay
              </button>
              <button
                onClick={confirmNavigation}
                className="text-sm px-4 py-2 rounded bg-[#000] text-white hover:bg-gray-800"
              >
                Leave
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DiscountPageForm;